import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { getPriorityColor, getUserInfo } from "../../utils/helpers";
import { useOpenLead } from "../../hooks/useOpenLead";
import { Delete, SquarePen } from "lucide-react";
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { RIDER_CLIENT_ID } from "../../utils/constants";

// Client IDs that should see the updated UI layout (Mobile, Name, Machine ID, Date & Time, Status)
const UPDATED_UI_CLIENT_IDS = [
  "69afdc065323c68fd76cdabf", // Client ID for updated card layout
];

const EMPTY_COLUMN = { deals: [], total: 0, loadingMore: false };

export const StageColumn = ({
  stage,
  column = EMPTY_COLUMN,
  onLoadMore,
  onDragOver,
  onDrop,
  onDragStart,
  onEditStage,
  onDeleteStage,
}) => {
  const openLead = useOpenLead();
  const { userId, userRole } = getUserInfo();
  // Viewers can look at the pipeline but can't drag leads between stages
  const canMoveDeals = userRole !== "viewer";

  // Leads are loaded from the server a page at a time (see useLeadData)
  const paginatedDeals = column.deals;
  const remaining = Math.max(column.total - column.deals.length, 0);

  // Load the next page when scrolled near the bottom of the column
  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (remaining > 0 && !column.loadingMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      onLoadMore?.();
    }
  };

  return (
    <Box
      onDragOver={onDragOver}
      onDrop={(e) => canMoveDeals && onDrop(e, stage._id)}
      sx={{
        minWidth: 220,
        maxWidth: 220,
        bgcolor: "#F9FAFB",
        borderRadius: 2,
        p: 2,
        border: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        height: "80vh", // 🔒 FIXED HEIGHT
        flexShrink: 0,
      }}
    >
      {/* ================= STAGE HEADER ================= */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} fontSize="0.95rem">
            {stage.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {column.total} deals
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            onClick={() => onEditStage?.(stage)}
            sx={{
              minWidth: "auto",
              px: 1,
              py: 0.5,
              color: "#6B7280",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <SquarePen size={18} />
          </Button>

          <Button
            size="small"
            onClick={() => onDeleteStage?.(stage)}
            sx={{
              minWidth: "auto",
              px: 1,
              py: 0.5,
              color: "#EF4444",
              "&:hover": { bgcolor: "rgba(239,68,68,0.1)" },
            }}
          >
            <Delete size={18} />
          </Button>
        </Stack>
      </Box>

      {/* ================= DEALS LIST (SCROLLABLE) ================= */}
      <Box
        onScroll={handleScroll}
        sx={{
          flex: 1,
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D1D5DB",
            borderRadius: 4,
          },
        }}
      >
        {paginatedDeals.length === 0 ? (
          <Box
            sx={{
              minHeight: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#9CA3AF",
            }}
          >
            <Typography variant="caption">No deals in this stage</Typography>
          </Box>
        ) : (
          paginatedDeals.map((deal) => {
            const isUpdatedUi = UPDATED_UI_CLIENT_IDS.includes(userId);
            const priorityColors = getPriorityColor(deal.priority);

            // Rider client: the card is keyed on rider identity, not a phone number.
            if (userId === RIDER_CLIENT_ID) {
              return (
                <Card
                  key={deal._id || deal.id}
                  draggable={canMoveDeals}
                  onDragStart={(e) => onDragStart?.(e, deal._id || deal.id)}
                  onClick={() => openLead(deal._id || deal.id)}
                  sx={{
                    cursor: "pointer",
                    borderLeft: `4px solid ${priorityColors.main}`,
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 2 },
                    minHeight: 110,
                    flexShrink: 0,
                  }}
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="0.85rem"
                      color="primary.main"
                      sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
                    >
                      <PermIdentityIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                      {deal.riderId || "—"}
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                      sx={{ mb: 0.5, fontSize: "0.8rem" }}
                    >
                      {deal.riderName || deal.dealName || "—"}
                    </Typography>

                    {deal.mobile && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem", display: "flex", alignItems: "center" }}
                      >
                        <PhoneIphoneIcon sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                        {deal.mobile}
                      </Typography>
                    )}

                    {deal.riderPlatform && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        <strong>Platform:</strong> {deal.riderPlatform}
                      </Typography>
                    )}

                    {deal.bikeNo && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5, fontSize: "0.75rem", display: "flex", alignItems: "center" }}
                      >
                        <TwoWheelerIcon sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                        {deal.bikeNo}
                      </Typography>
                    )}

                    {deal.currentStore && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        <strong>Store:</strong> {deal.currentStore}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, fontSize: "0.75rem" }}
                    >
                      <strong>Date & Time:</strong> {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "—"}
                    </Typography>

                    {deal.riderStatus && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={deal.riderStatus}
                          size="small"
                          sx={{
                            bgcolor: deal.riderStatus === "Justtawk" ? "#dbeafe" : "#dcfce7",
                            color: deal.riderStatus === "Justtawk" ? "#1e40af" : "#15803d",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            height: 18,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }

            if (isUpdatedUi) {
              return (
                <Card
                  key={deal._id || deal.id}
                  draggable={canMoveDeals}
                  onDragStart={(e) => onDragStart?.(e, deal._id || deal.id)}
                  onClick={() => openLead(deal._id || deal.id)}
                  sx={{
                    cursor: "pointer",
                    borderLeft: `4px solid ${priorityColors.main}`,
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 2 },
                    minHeight: 110,
                    flexShrink: 0,
                  }}
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="0.85rem"
                      color="primary.main"
                      sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
                    >
                      <PhoneIphoneIcon sx={{ mr: 0.5, fontSize: "1rem" }} />{deal.mobile || "—"}
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                      sx={{ mb: 0.5, fontSize: "0.8rem" }}
                    >
                      {deal.name || deal.dealName || "—"}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, fontSize: "0.75rem" }}
                    >
                      <strong>Machine ID:</strong> {deal.machineNumber || "—"}
                    </Typography>

                    {deal.errorCode && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        display="block"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        <strong>Error Code:</strong> {deal.errorCode}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, fontSize: "0.75rem" }}
                    >
                      <strong>Date & Time:</strong> {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "—"}
                    </Typography>

                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const status = deal.complaintStatus;
                        if (!status) {
                          return null;
                        }
                        const s = status.toLowerCase();
                        let bgcolor = "#f1f5f9";
                        let color = "#475569";
                        if (s.includes("resolved") || s.includes("received")) {
                          bgcolor = "#dcfce7";
                          color = "#15803d";
                        } else if (s.includes("credit") || s.includes("refund")) {
                          bgcolor = "#dbeafe";
                          color = "#1e40af";
                        }
                        return (
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              bgcolor,
                              color,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              height: 18,
                              borderRadius: 1,
                            }}
                          />
                        );
                      })()}
                    </Box>

                    {(deal.email || deal.customerEmailAddress) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={deal.email || deal.customerEmailAddress}
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            fontSize: "0.65rem",
                            height: 18,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    )}

                  </CardContent>
                </Card>
              );
            }

            // Previous layout for all other clients
            return (
              <Card
                key={deal._id || deal.id}
                draggable={canMoveDeals}
                onDragStart={(e) => onDragStart?.(e, deal._id || deal.id)}
                onClick={() => openLead(deal._id || deal.id)}
                sx={{
                  cursor: "pointer",
                  borderLeft: `4px solid ${priorityColors.main}`,
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: 2 },
                  minHeight: 110,
                  flexShrink: 0,
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="0.85rem"
                      sx={{
                        display: 'flex',
                        alignItems: "center"
                      }}
                    >
                      <PhoneIphoneIcon sx={{ mr: 0.5, fontSize: "1rem", color: "gray" }} /> {deal.mobile}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    // color="text.secondary"
                    display="block"
                    mb={deal.machineNumber ? 0.5 : 1}
                    sx={{
                      display: 'flex',
                      alignItems: "center"
                    }}
                  >
                    <PermIdentityIcon sx={{ mr: 0.5, fontSize: "1rem", color: "gray" }} /> {deal.name || deal.dealName}
                  </Typography>

                  {deal.machineNumber && (
                    <Chip
                      label={`Machine #${deal.machineNumber}`}
                      size="small"
                      sx={{
                        bgcolor: "#eff6ff",
                        color: "#1e40af",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        height: 18,
                        mb: 1,
                        borderRadius: 1,
                      }}
                    />
                  )}

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, mb: 1 }}>
                    <Typography
                      variant="caption"
                      // color="text.secondary"
                      sx={{ fontSize: "0.7rem", display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                      {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "—"}
                    </Typography>
                    {deal.closingDate && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                      >
                        Closing: {new Date(deal.closingDate).toLocaleDateString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                    )}
                  </Box>

                  {(deal.email || deal.customerEmailAddress) && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={deal.email || deal.customerEmailAddress}
                        size="small"
                        sx={{
                          bgcolor: "#f1f5f9",
                          color: "#475569",
                          fontSize: "0.65rem",
                          height: 18,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  )}

                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      {/* ================= LOAD MORE (FIXED) ================= */}
      {remaining > 0 && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#F9FAFB",
          }}
        >
          <Button
            size="small"
            onClick={onLoadMore}
            disabled={column.loadingMore}
            startIcon={column.loadingMore ? <CircularProgress size={14} /> : null}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
          >
            {column.loadingMore
              ? "Loading…"
              : `Load more (${remaining} remaining)`}
          </Button>
        </Box>
      )}
    </Box>
  );
};
